import { FrameOption } from '@renderer/types/configState'

/**
 * Maps each DISTINCT photo slot type in a frame (e.g. 'photo-1', 'photo-3', 'photo-4') to a
 * sequential index into the selected/captured photos array, ordered by the photo NUMBER
 * (numeric-aware, so 'photo-2' < 'photo-10').
 *
 * A session captures/selects exactly as many photos as there are DISTINCT photo types in the
 * frame, and it fills them in numeric photo-type order: FrameBasedCapture (and the other capture
 * flows) build their capture sequence via
 * `Array.from(uniquePhotoTypes).sort(localeCompare(numeric))`, so selectedPhotos[i] is the photo
 * captured for the i-th numerically-sorted distinct type. The slot-type → photo-index mapping here
 * must match that exact order.
 *
 * Frame templates can legitimately repeat a type (the same photo shown in several slots, e.g.
 * 'photo-1' twice) and can have GAPS in their numbering (e.g. 'photo-1','photo-3','photo-4' with no
 * 'photo-2').
 *
 * - The original code assumed a contiguous `photo-${i+1}` sequence, which left any slot whose
 *   number exceeded the distinct count (e.g. 'photo-4' when only 3 distinct types exist) unmapped —
 *   producing an empty trailing slot and a `missing_photo_state` strip-export failure.
 * - A later fix derived the index from FIRST-OCCURRENCE order in the `images` array. That fixed the
 *   gap case but assumed the array is stored in numeric order. Frames whose `images` array is in
 *   visual/layout order (e.g. a two-column strip stored as photo-1, photo-4, photo-2, photo-5,
 *   photo-3, photo-6) then mismapped every non-first slot — photo-4 got selectedPhotos[1] (the 2nd
 *   captured photo) instead of selectedPhotos[3] — so photos landed in the wrong placeholders
 *   (numbering 1,4,2,5,3,6 rendered as 1,2,3,4,5,6).
 *
 * Sorting the distinct types by photo number and then compacting to sequential indices matches the
 * capture order exactly: it is identical to `photo-N → N-1` for contiguous frames regardless of the
 * array's storage order, and it still fills every slot for duplicate/gapped numbering.
 */
export function buildPhotoTypeToSelectedIndex(
  images: { type: string }[]
): Record<string, number> {
  const distinctTypes = Array.from(
    new Set(images.map((img) => img.type).filter((type) => type?.startsWith('photo-')))
  ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))

  const map: Record<string, number> = {}
  distinctTypes.forEach((type, index) => {
    map[type] = index
  })
  return map
}

export const frameUtils = {
  getPhotoRatio(frameImage?: { width: number; height: number }): number {
    if (frameImage) {
      return frameImage.width / frameImage.height
    }
    return 3 / 4 // Fallback to default ratio
  },

  getFramePhotoRatios(frame?: FrameOption): number[] {
    if (!frame) return []

    return frame.drawParams.images
      .filter((img) => img.type.startsWith('photo-'))
      .map((img) => img.width / img.height)
  }
}
